import React, { useEffect, useState } from "react";
import { Routes, Route, Link, useParams, Navigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CounselorReadOnlyView } from "@/pages/CounselorDashboard";
import {
  Users, GraduationCap, LogOut, Plus, Pencil, Trash2, KeyRound, Eye, Power, ChevronLeft,
  ClipboardList, Target, MessagesSquare, AlertCircle, ShieldCheck, CalendarDays, MessageCircle, ClipboardCheck,
} from "lucide-react";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import useUnreadMessages from "@/hooks/useUnreadMessages";
import { NotificationsPanel, MonthlyActivityChart, CounselorEvaluation } from "@/components/SupervisorInsights";
import MessageCenter from "@/components/MessageCenter";
import FollowUpSupervisor from "@/components/followup/Supervisor";
import VisitLog from "@/components/VisitLog";
import ContactBook from "@/components/ContactBook";
import GeneralManagerAccounts from "@/components/GeneralManagerAccounts";
import CounselorLoginHistory from "@/components/CounselorLoginHistory";
import PrincipalApprovalNotices from "@/components/PrincipalApprovalNotices";

function Sidebar() {
  const { user, logout } = useAuth();
  const unreadMessages = useUnreadMessages(user?.id);
  const accountName = user?.username === "admin"
    ? user.name?.replace(/^مسؤول النظام\s*/, "")
    : user?.username === "adam"
      ? user.name?.replace(/^المشرف الإرشادي\s*/, "")
      : user?.name;
  const accountRole = user?.role === "general_manager"
    ? "مدير عام — اطلاع فقط"
    : user?.role === "principal"
      ? (user?.gender === "female" ? "مديرة المدرسة" : "مدير المدرسة")
      : user?.username === "admin"
        ? "مسؤول النظام"
        : user?.username === "adam" || user?.can_evaluate_counselors
          ? "المشرف الإرشادي"
          : user?.role === "admin"
            ? "مسؤول النظام"
            : "مرشد طلابي";
  return (
    <aside className="w-72 shrink-0 bg-white border-s border-[#e6e2d8] flex flex-col h-screen sticky top-0">
      <div className="p-4 border-b border-[#e6e2d8]">
        <Link to="/supervisor" className="block">
          <img
            src="/branding/sidebar-school-marks.png"
            alt="شركة كيان النموذجية"
            data-platform-logo="true"
            data-testid="supervisor-kayan-logo"
            className="w-full h-auto object-contain mb-2"
          />
          <div className="text-center">
            <div className="font-bold text-[#1A201C] text-sm" style={{ fontFamily: "IBM Plex Sans Arabic" }}>منصة الإرشاد الطلابي</div>
            <div className="text-xs text-[#4A554E]">شركة كيان النموذجية — {user?.role === "general_manager" ? "الاطلاع العام" : "لوحة المشرف"}</div>
          </div>
        </Link>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-0.5">
        {user?.role === "admin" && user?.can_evaluate_counselors && (
          <Link to="/supervisor/counselor-logins" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
            <CalendarDays className="w-4 h-4" /> متابعة دخول المرشدين
          </Link>
        )}
        {(user?.role === "principal" || (user?.role === "admin" && user?.can_evaluate_counselors)) && (
          <Link to="/supervisor/principal-logins" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
            <CalendarDays className="w-4 h-4" /> زيارات المدير للمنصة
          </Link>
        )}
        <Link to="/supervisor" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#3E6A56]/10 text-[#3E6A56] font-semibold">
          <Users className="w-4 h-4" /> المرشدون
        </Link>
        {(user?.role === "admin" || user?.role === "principal" || user?.role === "general_manager") && (
          <>
            <Link to="/supervisor/communication" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
              <MessageCircle className="w-4 h-4" /> التواصل والرسائل
              {unreadMessages > 0 && (
                <span className="ms-auto inline-flex min-w-5 h-5 items-center justify-center rounded-full bg-[#B84B4B] px-1 text-[10px] font-bold text-white" aria-label={`${unreadMessages} رسائل غير مقروءة`}>
                  {unreadMessages}
                </span>
              )}
            </Link>
            {user?.role === "admin" && (
              <Link to="/supervisor/followup" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
                <ClipboardCheck className="w-4 h-4" /> متابعة الطالب (اعتماد)
              </Link>
            )}
            {user?.role === "admin" && !user?.scope && (
              <Link to="/supervisor/contacts" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
                <MessagesSquare className="w-4 h-4" /> جهات التواصل
              </Link>
            )}
            {user?.role === "admin" && !user?.scope && (
              <Link to="/supervisor/visits" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-[#4A554E] hover:bg-[#F9F8F6]">
                <ClipboardCheck className="w-4 h-4" /> الزيارات والتواصل
              </Link>
            )}
          </>
        )}
      </nav>
      <div className="p-4 border-t border-[#e6e2d8]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-[#3E6A56] text-white flex items-center justify-center font-bold text-sm">
            {accountName?.[0]}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-[#1A201C] truncate">{accountName}</div>
            <div className="text-xs text-[#4A554E]">{accountRole}</div>
          </div>
        </div>
        <ChangePasswordDialog
          trigger={
            <button className="w-full flex items-center justify-center gap-2 py-2 mb-2 rounded-xl text-sm text-[#3E6A56] hover:bg-[#3E6A56]/10 transition" data-testid="change-password-trigger">
              <KeyRound className="w-4 h-4" /> تغيير كلمة المرور
            </button>
          }
        />
        <button onClick={logout} data-testid="logout-button" className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm text-[#B84B4B] hover:bg-[#B84B4B]/10 transition">
          <LogOut className="w-4 h-4" /> تسجيل الخروج
        </button>
      </div>
    </aside>
  );
}

function CounselorFormDialog({ open, onOpenChange, initial, onSaved }) {
  const editing = !!initial;
  const [form, setForm] = useState({ username: "", name: "", password: "", gender: "male", school_key: "" });
  useEffect(() => {
    setForm(initial
      ? { username: initial.username, name: initial.name, password: "", gender: initial.gender || "male", school_key: initial.school_key || "" }
      : { username: "", name: "", password: "", gender: "male", school_key: "" });
  }, [initial, open]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { username: form.username, name: form.name, gender: form.gender, school_key: form.school_key || null };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${initial.id}`, payload);
        toast.success("تم تحديث المرشد");
      } else {
        await api.post("/users", { ...form, school_key: form.school_key || null });
        toast.success("تم إضافة المرشد");
      }
      onOpenChange(false); onSaved();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "IBM Plex Sans Arabic" }}>{editing ? "تعديل مرشد" : "إضافة مرشد جديد"}</DialogTitle>
          <DialogDescription>بيانات حساب المرشد الطلابي ومدرسته.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div>
            <Label className="mb-1.5 block">الاسم الكامل</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl bg-white border-[#e6e2d8]" data-testid="counselor-name-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">اسم المستخدم (بالإنجليزية)</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required className="rounded-xl bg-white border-[#e6e2d8]" data-testid="counselor-username-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">كلمة المرور {editing && "(اتركها فارغة إن لم ترغب بتغييرها)"}</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={8} className="rounded-xl bg-white border-[#e6e2d8]" data-testid="counselor-password-input" />
          </div>
          <div>
            <Label className="mb-1.5 block">النوع</Label>
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
              <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">مرشد</SelectItem>
                <SelectItem value="female">مرشدة</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1.5 block">المدرسة</Label>
            <Select value={form.school_key || "none"} onValueChange={(v) => setForm({ ...form, school_key: v === "none" ? "" : v })}>
              <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]" data-testid="counselor-school-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">غير محددة</SelectItem>
                <SelectItem value="abaq">عبق العلم العالمية</SelectItem>
                <SelectItem value="durat">درة الشرق العالمية</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-xl">إلغاء</Button>
            <Button type="submit" className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="counselor-form-save-button">حفظ</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PrincipalManagement() {
  const [principals, setPrincipals] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [principalToDelete, setPrincipalToDelete] = useState(null);
  const [form, setForm] = useState({ username: "", name: "", password: "", gender: "male", school_key: "abaq" });

  const load = async () => {
    try { setPrincipals((await api.get("/principals")).data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const start = (principal = null) => {
    setEditing(principal);
    setForm(principal
      ? { username: principal.username, name: principal.name, password: "", gender: principal.gender, school_key: principal.school_key }
      : { username: "", name: "", password: "", gender: "male", school_key: "abaq" });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const payload = { username: form.username, name: form.name, gender: form.gender, school_key: form.school_key };
        if (form.password) payload.password = form.password;
        await api.put(`/principals/${editing.id}`, payload);
        toast.success("تم تحديث مدير المدرسة");
      } else {
        await api.post("/principals", form);
        toast.success("تمت إضافة مدير المدرسة");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const toggle = async (principal) => {
    try {
      await api.put(`/principals/${principal.id}`, { active: !principal.active });
      toast.success("تم تحديث حالة الحساب");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const remove = async () => {
    if (!principalToDelete) return;
    try {
      await api.delete(`/principals/${principalToDelete.id}`);
      toast.success("تم حذف الحساب");
      setPrincipalToDelete(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
            مدراء المدارس
          </h2>
          <p className="text-sm text-[#4A554E] mt-1">إدارة حسابات المدراء وربط كل حساب بالمدرسة والقسم.</p>
        </div>
        <Button onClick={() => start()} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="add-principal-button">
          <ShieldCheck className="w-4 h-4 me-2" /> إضافة مدير
        </Button>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {principals.map((principal) => (
          <div key={principal.id} className="rounded-xl border border-[#e6e2d8] p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold text-[#1A201C]">{principal.name}</div>
                <div className="text-xs text-[#4A554E] font-mono mt-1">{principal.username}</div>
                <div className="text-xs text-[#4A554E] mt-2">
                  {principal.school_name || principal.school_key} — {principal.gender === "female" ? "بنات" : "بنين"}
                </div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full ${principal.active ? "bg-[#3E6A56]/10 text-[#3E6A56]" : "bg-[#B84B4B]/10 text-[#B84B4B]"}`}>
                {principal.active ? "نشط" : "معطل"}
              </span>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => start(principal)} className="rounded-xl border-[#e6e2d8]" data-testid={`edit-principal-${principal.username}`}>
                <Pencil className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => toggle(principal)} className="rounded-xl border-[#e6e2d8]">
                <Power className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setPrincipalToDelete(principal)} className="rounded-xl border-[#e6e2d8] text-[#B84B4B] hover:bg-[#B84B4B]/10">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "IBM Plex Sans Arabic" }}>{editing ? "تعديل مدير مدرسة" : "إضافة مدير مدرسة"}</DialogTitle>
            <DialogDescription>يُستخدم الحساب للعرض فقط ضمن المدرسة والقسم المحددين.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label className="mb-1.5 block">الاسم الكامل</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="rounded-xl bg-white border-[#e6e2d8]" /></div>
            <div><Label className="mb-1.5 block">اسم المستخدم</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required className="rounded-xl bg-white border-[#e6e2d8]" /></div>
            <div><Label className="mb-1.5 block">كلمة المرور {editing && "(اتركها فارغة دون تغيير)"}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required={!editing} minLength={8} className="rounded-xl bg-white border-[#e6e2d8]" /><p className="text-xs text-[#4A554E] mt-1">8 أحرف على الأقل.</p></div>
            <div>
              <Label className="mb-1.5 block">القسم</Label>
              <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="male">بنين</SelectItem><SelectItem value="female">بنات</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1.5 block">المدرسة</Label>
              <Select value={form.school_key} onValueChange={(v) => setForm({ ...form, school_key: v })}>
                <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="abaq">عبق العلم العالمية</SelectItem><SelectItem value="durat">درة الشرق العالمية</SelectItem></SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">إلغاء</Button>
              <Button type="submit" className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="principal-form-save-button">حفظ</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={Boolean(principalToDelete)} onOpenChange={(isOpen) => { if (!isOpen) setPrincipalToDelete(null); }}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تأكيد حذف الحساب</AlertDialogTitle>
            <AlertDialogDescription>هل تريد حذف حساب مدير المدرسة {principalToDelete?.name}؟ لا يمكن التراجع عن هذا الإجراء.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[#B84B4B] text-white hover:bg-[#963A3A]">حذف الحساب</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

function PrincipalLoginHistory() {
  const [days, setDays] = useState("30");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (range = days) => {
    setLoading(true);
    try { setEntries((await api.get(`/principals/login-history?days=${range}`)).data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const formatDate = (value) => new Intl.DateTimeFormat("ar-SA", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(value));

  return (
    <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-[#1A201C] flex items-center gap-2" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
            <CalendarDays className="w-5 h-5 text-[#3E6A56]" /> متابعة دخول مدراء المدارس
          </h2>
          <p className="text-sm text-[#4A554E] mt-1">يعرض كل دخول ناجح للمدير مع اليوم والتاريخ والوقت؛ زيارة الدخول لا تعني الاطلاع على السجلات أو اعتمادها.</p>
        </div>
        <Select value={days} onValueChange={(value) => { setDays(value); load(value); }}>
          <SelectTrigger className="w-40 rounded-xl bg-white border-[#e6e2d8]" data-testid="principal-login-range">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">آخر 7 أيام</SelectItem>
            <SelectItem value="30">آخر 30 يوماً</SelectItem>
            <SelectItem value="90">آخر 90 يوماً</SelectItem>
            <SelectItem value="365">آخر سنة</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {loading ? (
        <div className="text-sm text-[#4A554E]">جاري تحميل سجل الدخول...</div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl bg-[#F9F8F6] p-4 text-sm text-[#4A554E]">لا توجد عمليات دخول خلال الفترة المحددة.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[#e6e2d8] text-right text-[#4A554E]">
                <th className="p-3 font-medium">المدير</th>
                <th className="p-3 font-medium">اسم المستخدم</th>
                <th className="p-3 font-medium">المدرسة والقسم</th>
                <th className="p-3 font-medium">وقت الدخول</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id} className="border-b border-[#f0eee9] last:border-0">
                  <td className="p-3 font-semibold text-[#1A201C]">{entry.name}</td>
                  <td className="p-3 font-mono text-[#4A554E]">{entry.username}</td>
                  <td className="p-3 text-[#4A554E]">{entry.school_name || entry.school_key} — {entry.gender === "female" ? "بنات" : "بنين"}</td>
                  <td className="p-3 text-[#4A554E] whitespace-nowrap">{formatDate(entry.logged_in_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Overview() {
  const { user } = useAuth();
  const isPrincipal = user?.role === "principal";
  const isGeneralManager = user?.role === "general_manager";
  const isFullAdmin = user?.role === "admin" && !user?.scope;
  const canEvaluateCounselors = user?.can_evaluate_counselors === true;
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/overview"); setCounselors(data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (c) => {
    try { await api.put(`/users/${c.id}`, { active: !c.active }); toast.success("تم التحديث"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (c) => {
    if (!window.confirm(`حذف ${c.name}؟ سيتم حذف كل سجلاته.`)) return;
    try { await api.delete(`/users/${c.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const totals = counselors.reduce((acc, c) => {
    Object.entries(c.stats).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
    return acc;
  }, {});
  const overallTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  const overallCards = [
    { label: "إجمالي المرشدين", value: counselors.length, icon: Users, color: "#3E6A56" },
    { label: "البرامج المنفذة", value: totals.program || 0, icon: Target, color: "#DDA74A" },
    { label: "المواقف اليومية", value: totals.daily_incident || 0, icon: AlertCircle, color: "#3E6A56" },
    { label: "تواصل أولياء", value: totals.parent_contact || 0, icon: MessagesSquare, color: "#C97052" },
    { label: "إجمالي السجلات", value: overallTotal, icon: ClipboardList, color: "#3E6A56" },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div>
          <div className="text-sm text-[#3E6A56] font-medium mb-2">{isGeneralManager ? `المدير العام ${user?.name || ""} — اطلاع فقط` : isPrincipal ? `لوحة ${user?.gender === "female" ? "مديرة" : "مدير"} المدرسة — ${user?.school_name || ""}` : `المشرف ${user?.name || ""}`}</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
            المرشدون الطلابيون
          </h1>
          <p className="text-[#4A554E] mt-2">{isPrincipal || isGeneralManager ? "اطّلع على أعمال المرشدين وسجلاتهم (عرض فقط)." : "أدر حسابات المرشدين، وتصفح سجلاتهم بشكل شامل."}</p>
        </div>
        {!isPrincipal && !isGeneralManager && (
        <Button onClick={() => { setEditing(null); setDlgOpen(true); }} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="add-counselor-button">
          <Plus className="w-4 h-4 me-2" /> إضافة مرشد
        </Button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        {overallCards.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-2xl border border-[#e6e2d8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${s.color}15`, color: s.color }}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-3xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>{s.value}</div>
              <div className="text-xs text-[#4A554E] mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>

      {!isPrincipal && !isGeneralManager && <NotificationsPanel />}
      {isPrincipal && <PrincipalApprovalNotices />}

      {!isPrincipal && !isGeneralManager && !user?.scope && <PrincipalManagement />}
      {!isPrincipal && !isGeneralManager && !user?.scope && <PrincipalLoginHistory />}
      {isFullAdmin && user?.username === "admin" && (
        <>
          <GuidanceSupervisorAccount />
          <GeneralManagerAccounts />
        </>
      )}

      {!isGeneralManager && <div className="grid gap-6 xl:grid-cols-[1fr_380px]">
        <MonthlyActivityChart />
      </div>}

      {canEvaluateCounselors && <CounselorEvaluation />}

      <div>
        <h2 className="text-xl font-bold text-[#1A201C] mb-4" style={{ fontFamily: "IBM Plex Sans Arabic" }}>قائمة المرشدين</h2>
        {loading ? (
          <div className="text-[#4A554E]">جاري التحميل...</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {counselors.map((c) => (
              <div key={c.id} className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] p-5 hover:-translate-y-0.5 transition"
                data-testid={`counselor-card-${c.username}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-white ${c.gender === "female" ? "bg-[#C97052]" : "bg-[#3E6A56]"}`}>
                      {c.name[0]}
                    </div>
                    <div>
                      <div className="font-semibold text-[#1A201C]">{c.name}</div>
                      <div className="text-xs text-[#4A554E] font-mono">{c.username}</div>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${c.active ? "bg-[#3E6A56]/10 text-[#3E6A56]" : "bg-[#B84B4B]/10 text-[#B84B4B]"}`}>
                    {c.active ? "نشط" : "معطل"}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-[#F9F8F6] rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-[#C97052]">{c.stats.individual_session || 0}</div>
                    <div className="text-[10px] text-[#4A554E]">جلسات فردية</div>
                  </div>
                  <div className="bg-[#F9F8F6] rounded-lg p-2 text-center">
                    <div className="text-lg font-bold text-[#DDA74A]">{c.stats.program || 0}</div>
                    <div className="text-[10px] text-[#4A554E]">برامج</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Link to={`/supervisor/counselor/${c.id}`} className="flex-1">
                    <Button size="sm" className="w-full rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid={`view-counselor-${c.username}`}>
                      <Eye className="w-4 h-4 me-1" /> عرض الملف
                    </Button>
                  </Link>
                  {!isPrincipal && !isGeneralManager && (
                  <>
                  <Button size="sm" variant="outline" onClick={() => { setEditing(c); setDlgOpen(true); }} className="rounded-xl border-[#e6e2d8]" data-testid={`edit-counselor-${c.username}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => toggleActive(c)} className="rounded-xl border-[#e6e2d8]">
                    <Power className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => del(c)} className="rounded-xl border-[#e6e2d8] text-[#B84B4B] hover:bg-[#B84B4B]/10">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <CounselorFormDialog open={dlgOpen} onOpenChange={setDlgOpen} initial={editing} onSaved={load} />
    </div>
  );
}

function CounselorView() {
  const { id } = useParams();
  const [counselor, setCounselor] = useState(null);
  useEffect(() => {
    api.get("/admin/overview").then((r) => {
      const c = r.data.find((x) => x.id === id);
      setCounselor(c);
    });
  }, [id]);
  if (!counselor) return <div className="p-10 text-[#4A554E]">جاري التحميل...</div>;
  return <CounselorReadOnlyView counselor={counselor} />;
}

function GuidanceSupervisorAccount() {
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);

  const resetPassword = async (event) => {
    event.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("كلمتا المرور غير متطابقتين");
      return;
    }
    setSavingPassword(true);
    try {
      await api.put("/admin/guidance-supervisor-account/password", { new_password: newPassword });
      toast.success("تم تغيير كلمة مرور المشرف الإرشادي");
      setPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(formatErr(error));
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border border-[#e6e2d8] p-5 shadow-[0_8px_30px_rgb(0,0,0,0.03)]" data-testid="guidance-supervisor-account">
      <h2 className="text-xl font-bold text-[#1A201C] mb-4" style={{ fontFamily: "IBM Plex Sans Arabic" }}>حساب المشرف الإرشادي</h2>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-[#3E6A56] text-white flex items-center justify-center font-bold">آ</div>
          <div>
            <div className="font-semibold text-[#1A201C]">آدم المحنبي</div>
            <div className="text-xs text-[#4A554E]">المشرف الإرشادي · اسم المستخدم <span className="font-mono">adam</span></div>
          </div>
        </div>
        <span className="text-xs px-2 py-1 rounded-full whitespace-nowrap bg-[#3E6A56]/10 text-[#3E6A56]">نشط</span>
      </div>
      <div className="mt-4 border-t border-[#e6e2d8] pt-4">
        <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="rounded-xl border-[#e6e2d8]" data-testid="reset-guidance-supervisor-password-trigger">
              <KeyRound className="w-4 h-4 me-2" /> تغيير كلمة المرور
            </Button>
          </DialogTrigger>
          <DialogContent dir="rtl">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "IBM Plex Sans Arabic" }}>تغيير كلمة مرور المشرف الإرشادي</DialogTitle>
              <DialogDescription>عيّن كلمة مرور جديدة لحساب آدم. يجب أن تتكون من 8 أحرف على الأقل.</DialogDescription>
            </DialogHeader>
            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label htmlFor="guidance-supervisor-new-password" className="mb-1.5 block">كلمة المرور الجديدة</Label>
                <Input
                  id="guidance-supervisor-new-password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  required
                  minLength={8}
                  className="rounded-xl bg-white border-[#e6e2d8]"
                  data-testid="guidance-supervisor-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="guidance-supervisor-confirm-password" className="mb-1.5 block">تأكيد كلمة المرور الجديدة</Label>
                <Input
                  id="guidance-supervisor-confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  required
                  minLength={8}
                  className="rounded-xl bg-white border-[#e6e2d8]"
                  data-testid="guidance-supervisor-confirm-password-input"
                />
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={() => setPasswordDialogOpen(false)} className="rounded-xl">إلغاء</Button>
                <Button type="submit" disabled={savingPassword} className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid="save-guidance-supervisor-password-button">
                  {savingPassword ? "جاري الحفظ..." : "حفظ كلمة المرور"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}

export default function SupervisorDashboard() {
  const { user } = useAuth();
  const shell = (content) => (
    <div className="platform-shell min-h-screen flex" dir="rtl" data-platform-shell>
      <div className="hidden lg:block"><Sidebar /></div>
      <main className="flex-1 p-6 sm:p-8 lg:p-10 max-w-7xl">{content}</main>
    </div>
  );
  return (
    <Routes>
      <Route index element={
        <div className="platform-shell min-h-screen flex" dir="rtl" data-platform-shell>
          <div className="hidden lg:block"><Sidebar /></div>
          <main className="flex-1 p-6 sm:p-8 lg:p-10 max-w-7xl"><Overview /></main>
        </div>
      } />
      <Route path="communication" element={shell(<MessageCenter />)} />
      <Route path="contacts" element={shell(
        <ContactRoute />
      )} />
      <Route path="visits" element={shell(<VisitLog />)} />
      <Route path="followup" element={user?.role === "admin" ? shell(<FollowUpSupervisor />) : <Navigate to="/supervisor" replace />} />
      <Route path="principal-logins" element={shell(<PrincipalLoginHistory />)} />
      <Route path="counselor-logins" element={shell(<CounselorLoginHistory />)} />
      <Route path="counselor/:id/*" element={<CounselorView />} />
    </Routes>
  );
}

function ContactRoute() {
  const { user } = useAuth();
  return user?.role === "admin" && !user?.scope ? <ContactBook /> : <Navigate to="/supervisor" replace />;
}
